import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';
import { distributionService } from '../../../services/distributionService';
import MantleDataGrid, { gcell } from '../../../components/MantleDataGrid';
import PickerPopover from '../../../components/PickerPopover';
import SearchSelect from '../../../components/SearchSelect';

// =============================================================================
// Budget Setup > Variable Expenses  (Distribution)
//
// The catalogue of variable expense lines and where each one posts. This is the
// ONLY place a line is created: the site pages can take a line up, point it at
// a different driver and enter rates, but they cannot invent one. That split is
// the whole point — deciding that "Shrink Wrap" is GL 6210 is a chart-of-
// accounts decision with one owner, and leaving it on the capture screen is how
// six sites end up with six spellings of the same expense against four
// accounts.
//
// CROSS-SITE, PER VERSION. The GL account applies everywhere by construction —
// there is no per-site GL override and none is wanted. The cost centre and the
// driver are DEFAULTS: a site may override either, because a DC is usually its
// own cost centre and a cold store may bill floor area where everyone else
// bills throughput. Whatever a site does not override, it inherits from here.
// =============================================================================

const MODULE = 'MOD_4_MOVE';

const BLANK = { line_code: '', line_name: '', account_code: '', cost_center_code: '', driver_code: '' };

const T_INACTIVE = { textDark: '#94a3b8' };
const T_WARN = { textDark: '#b45309' };

const VariableExpenseSetup = () => {
  const navigate = useNavigate();
  const { selectedVersion } = useAppContext();

  const [lines, setLines] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  const [dirty, setDirty] = useState({});      // { line_code: true }
  const [picker, setPicker] = useState(null);  // { code, field, x, y }
  const [entry, setEntry] = useState(BLANK);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');

  const ready = !!selectedVersion;

  const accLabel = useCallback((code) => {
    if (!code) return '';
    const a = accounts.find((x) => x.code === code);
    return a ? `${a.code} - ${a.description}` : code;
  }, [accounts]);
  const ccLabel = useCallback((code) => {
    if (!code) return '';
    const c = costCenters.find((x) => x.code === code);
    return c ? `${c.code} - ${c.description}` : code;
  }, [costCenters]);
  const driverLabel = useCallback((code) => {
    if (!code) return '';
    const d = drivers.find((x) => x.code === code);
    return d ? d.description : code;
  }, [drivers]);

  const load = useCallback(async () => {
    if (!ready) { setLines([]); return; }
    setIsLoading(true); setError(null);
    try {
      const res = await distributionService.getExpenseCatalogue(MODULE);
      if (!res.success) throw new Error(res.message);
      setLines((res.data.lines || []).map((l) => ({ ...l, isNew: false })));
      setDrivers(res.data.drivers || []);
      setAccounts(res.data.accounts || []);
      setCostCenters(res.data.costCenters || []);
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load the expense catalogue.');
      setLines([]);
    } finally { setIsLoading(false); }
  }, [ready]);

  useEffect(() => { load(); }, [load]);

  const linesRef = useRef([]);
  linesRef.current = lines;

  // ---- Grid ----------------------------------------------------------------------
  const gcols = useMemo(() => ([
    { id: 'line_code', title: 'Code', width: 130 },
    { id: 'line_name', title: 'Expense Line', width: 230 },
    { id: 'account_code', title: 'GL Account', width: 250 },
    { id: 'cost_center_code', title: 'Default Cost Centre', width: 210 },
    { id: 'driver_code', title: 'Default Cost Driver', width: 190 },
    { id: 'is_active', title: 'Active', width: 70 },
    { id: 'site_count', title: 'Sites', width: 64 },
    { id: 'del', title: '', width: 36 },
  ]), []);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l) return gcell.text('', { readonly: true });
    const dim = l.is_active ? undefined : T_INACTIVE;

    switch (id) {
      // The code is the identity a site's rows hang off, so it is fixed once
      // the line exists — renaming it in place would orphan them.
      case 'line_code': return gcell.text(l.line_code, { readonly: true, theme: dim });
      case 'line_name': return gcell.text(l.line_name, { theme: dim });
      case 'account_code':
        // An unmapped line still budgets, it just cannot post. Amber rather
        // than an error: it is a normal in-progress state.
        return gcell.text(accLabel(l.account_code) || '— not mapped —',
          { readonly: true, theme: l.account_code ? dim : T_WARN });
      case 'cost_center_code':
        return gcell.text(ccLabel(l.cost_center_code) || '—', { readonly: true, theme: dim });
      case 'driver_code':
        return gcell.text(driverLabel(l.driver_code) || '— none —',
          { readonly: true, theme: l.driver_code ? dim : T_WARN });
      case 'is_active': return gcell.boolean(!!l.is_active);
      case 'site_count':
        return gcell.number(l.site_count || 0, { readonly: true, display: String(l.site_count || 0), theme: dim });
      case 'del': return gcell.text('✕', { readonly: true, theme: { textDark: '#94a3b8' } });
      default: return gcell.text('', { readonly: true });
    }
  }, [colIds, accLabel, ccLabel, driverLabel]);

  const patch = useCallback((code, fields) => {
    setLines((prev) => prev.map((l) => (l.line_code === code ? { ...l, ...fields } : l)));
    setDirty((prev) => ({ ...prev, [code]: true }));
    setNote('');
  }, []);

  const onCellEdited = useCallback(([col, row], newValue) => {
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l || id !== 'line_name') return;
    const v = String(newValue.data ?? '').trim();
    if (!v) return;
    patch(l.line_code, { line_name: v });
  }, [colIds, patch]);

  const removeLine = useCallback(async (l) => {
    if (l.isNew) {
      setLines((prev) => prev.filter((x) => x.line_code !== l.line_code));
      setDirty((prev) => { const d = { ...prev }; delete d[l.line_code]; return d; });
      return;
    }
    const inUse = (l.site_count || 0) > 0;
    const msg = inUse
      ? `"${l.line_name}" is in use at ${l.site_count} site${l.site_count === 1 ? '' : 's'}. `
        + 'Deleting it removes their rates as well. Retiring it with the Active flag keeps the history — delete anyway?'
      : `Delete "${l.line_name}"?`;
    if (!window.confirm(msg)) return;
    setError(null);
    try {
      await distributionService.deleteExpenseCatalogueLine(l.line_code, { module: MODULE, force: inUse });
      setNote(`Deleted ${l.line_code}.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete this line.');
    }
  }, [load]);

  const onCellClicked = useCallback(([col, row], event) => {
    const id = colIds[col];
    const l = linesRef.current[row];
    if (!l) return;
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'account_code') setPicker({ code: l.line_code, field: 'account_code', ...pos });
    else if (id === 'cost_center_code') setPicker({ code: l.line_code, field: 'cost_center_code', ...pos });
    else if (id === 'driver_code') setPicker({ code: l.line_code, field: 'driver_code', ...pos });
    else if (id === 'is_active') patch(l.line_code, { is_active: !l.is_active });
    else if (id === 'del') removeLine(l);
  }, [colIds, patch, removeLine]);

  // ---- Inline add line ---------------------------------------------------------
  const setE = (k, v) => setEntry((prev) => ({ ...prev, [k]: v }));

  const newCode = entry.line_code.trim().toUpperCase();
  const dup = newCode && lines.some((l) => l.line_code === newCode);
  const entryProblem =
    !newCode ? 'Enter a code.'
    : dup ? 'That code already exists.'
    : !entry.line_name.trim() ? 'Enter a name.'
    : !entry.account_code ? 'Pick a GL account.'
    : '';

  const addLine = () => {
    if (entryProblem) return;
    setLines((prev) => [...prev, {
      line_code: newCode,
      line_name: entry.line_name.trim(),
      account_code: entry.account_code,
      cost_center_code: entry.cost_center_code || '',
      driver_code: entry.driver_code || '',
      is_active: true,
      sort_order: prev.length,
      notes: '',
      site_count: 0,
      isNew: true,
    }]);
    setDirty((prev) => ({ ...prev, [newCode]: true }));
    setEntry(BLANK);
    setNote('');
  };

  // ---- Save ----------------------------------------------------------------------
  const dirtyCount = Object.keys(dirty).length;

  const save = useCallback(async () => {
    if (!dirtyCount || isSaving) return;
    setIsSaving(true); setError(null); setNote('');
    try {
      const payload = linesRef.current.filter((l) => dirty[l.line_code]).map((l) => ({
        line_code: l.line_code,
        line_name: l.line_name,
        account_code: l.account_code,
        cost_center_code: l.cost_center_code,
        driver_code: l.driver_code,
        is_active: l.is_active,
        sort_order: l.sort_order,
        notes: l.notes,
      }));
      const res = await distributionService.saveExpenseCatalogue({ module_code: MODULE, lines: payload });
      if (!res.success) throw new Error(res.message);
      setNote(`Saved ${payload.length} line${payload.length === 1 ? '' : 's'}.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save the expense catalogue.');
    } finally { setIsSaving(false); }
  }, [dirtyCount, isSaving, dirty, load]);

  const toolbar = useMemo(() => (
    <button type="button" onClick={save} disabled={isSaving || !dirtyCount}
      className={`px-3 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        dirtyCount
          ? 'border-emerald-700 bg-emerald-600 text-white font-semibold hover:bg-emerald-700'
          : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
      {isSaving ? 'Saving…' : 'Save'}
    </button>
  ), [isSaving, dirtyCount, save]);

  const pickerOptions = useMemo(() => {
    if (!picker) return [];
    if (picker.field === 'account_code') return accounts;
    if (picker.field === 'cost_center_code') return costCenters;
    return drivers.map((d) => ({ code: d.code, label: d.description, hint: d.driver_uom }));
  }, [picker, accounts, costCenters, drivers]);

  const inputClass =
    'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 '
    + 'text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-5 pb-3">
        <button onClick={() => navigate('/distribution')}
          className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
          <ArrowLeft className="w-3.5 h-3.5" /> Distribution
        </button>
        <h1 className="m-0 mt-1 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Budget Setup · Variable Expenses
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The catalogue of variable expense lines and where each posts. Defined once here for every site — the
          only place a line is created. Sites take a line up and enter rates on Warehousing → Variable Running Costs.
        </p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      {ready && (
        <div className="shrink-0 px-6 pb-3">
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Code <span className="text-red-600 dark:text-red-400">*</span></span>
              <input className={`${inputClass} w-36 font-mono uppercase`} maxLength={50}
                value={entry.line_code} onChange={(e) => setE('line_code', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Expense line <span className="text-red-600 dark:text-red-400">*</span></span>
              <input className={`${inputClass} w-56`} maxLength={255}
                value={entry.line_name} onChange={(e) => setE('line_name', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>GL account <span className="text-red-600 dark:text-red-400">*</span></span>
              <SearchSelect className="w-64" value={entry.account_code} onChange={(v) => setE('account_code', v)}
                options={accounts.map((a) => ({ code: a.code, name: a.description }))}
                placeholder="Select account…" emptyText="No account matches." />
            </div>
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Default cost centre</span>
              <SearchSelect className="w-52" value={entry.cost_center_code} onChange={(v) => setE('cost_center_code', v)}
                options={costCenters.map((c) => ({ code: c.code, name: c.description }))}
                placeholder="(None)" clearLabel="(None)" emptyText="No cost centre matches." />
            </div>
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Default driver</span>
              <SearchSelect className="w-48" value={entry.driver_code} onChange={(v) => setE('driver_code', v)}
                options={drivers.map((d) => ({ code: d.code, name: d.description }))}
                placeholder="(None)" clearLabel="(None)" emptyText="No driver matches." />
            </div>
            <button type="button" onClick={addLine} disabled={!!entryProblem} title={entryProblem || 'Add line'}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white mb-0.5 ${
                entryProblem ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              <Plus className="w-4 h-4" /> Add
            </button>
            {note && <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mb-3">{note}</span>}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : isLoading ? (
            <div className="p-4 text-xs text-slate-400">Loading…</div>
          ) : lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No expense lines yet — add one on the line above.</div>
          ) : (
            <MantleDataGrid
              gridId="dist-expense-catalogue"
              fitHeight
              columns={gcols}
              rowCount={lines.length}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              freezeColumns={2}
              hint="Click GL Account, Cost Centre or Cost Driver to change it; the name edits inline. Sites shows how many sites have taken the line up. ✕ deletes it everywhere."
              toolbar={toolbar}
            />
          )}
        </div>
      </div>

      {picker && (
        <PickerPopover
          key={`${picker.code}-${picker.field}`}
          anchor={picker}
          options={pickerOptions}
          value={lines.find((l) => l.line_code === picker.code)?.[picker.field]}
          onPick={(code) => { patch(picker.code, { [picker.field]: code }); setPicker(null); }}
          // GL account has no clear row: a line with no account cannot post,
          // and clearing one that sites are already using is not an accident
          // worth making one click away.
          onClear={picker.field === 'account_code' ? undefined
            : () => { patch(picker.code, { [picker.field]: '' }); setPicker(null); }}
          clearLabel="(None)"
          onClose={() => setPicker(null)}
        />
      )}

      <p className="shrink-0 px-6 pb-4 m-0 text-[11px] text-slate-400">
        The GL account applies at every site. Cost centre and driver are defaults a site may override —
        whatever a site leaves blank, it inherits from here. Retiring a line with the Active flag keeps its
        history; deleting one removes it from every site that had taken it up.
      </p>
    </div>
  );
};

export default VariableExpenseSetup;
